/**
 * Authentication Hook - Simplified for Current Architecture
 *
 * Single Responsibility: Provide unified auth interface to components
 * Separation of Concerns: Uses auth context for state and service hooks for actions
 * DRY: Eliminates duplicate API calls by using centralized context
 */

import { useMemo, useCallback } from "react";
import { useAuthContext } from "@/contexts/auth-context";
import { useRouter } from "next/navigation";
import { AuthRedirectService } from "@/lib/services/auth-redirect-service";
import { useAuthService } from "@/hooks/services/use-auth-service";
import { User } from "@/lib/types";

// Type definitions for backward compatibility
export interface AuthCredentials {
  email: string;
  password: string;
}

export interface SignupData {
  email: string;
  password: string;
  confirmPassword: string;
  fullName?: string;
}

export interface AuthState {
  isLoading: boolean;
  isGoogleLoading: boolean;
  error: string | null;
  isAuthenticated: boolean;
  user: User | null;
  sessionStatus: string;
}

export interface AuthActions {
  loginWithCredentials: (credentials: AuthCredentials) => Promise<void>;
  signupWithCredentials: (data: SignupData) => Promise<void>;
  signOut: () => Promise<void>;
  clearError: () => void;
  requireAuth: (redirectTo?: string) => void;
}

export interface UseAuthReturn {
  state: AuthState;
  actions: AuthActions;
  isInPublicOrganization: boolean;
}

/**
 * Simplified Authentication Hook
 *
 * Provides backward compatibility interface while using the refactored architecture:
 * - State from auth context
 * - Actions using static AuthService methods
 * - Simplified implementation focused on most common use cases
 */
export function useAuth(): UseAuthReturn {
  const router = useRouter();
  const { state: contextState, actions: contextActions } = useAuthContext();
  const authService = useAuthService();

  // Create simplified state for backward compatibility
  const state = useMemo(
    (): AuthState => ({
      isLoading: contextState.auth.isLoading,
      isGoogleLoading: false, // Simplified - no separate Google loading state
      error: contextState.auth.error,
      isAuthenticated: contextState.auth.isAuthenticated,
      user: contextState.auth.user,
      sessionStatus: contextState.auth.sessionStatus,
    }),
    [contextState.auth]
  );

  /**
   * Send the human to where their work is.
   *
   * An explicit destination (they were bounced here from a protected page)
   * always wins. Otherwise an organisation administrator or publication
   * reviewer lands in Organisation Administration and everyone else lands on
   * their agents. The capability read is an affordance: if it fails we send
   * them to /agents, because a wrong first page is a navigation annoyance and
   * every admin surface re-authorizes anyway.
   */
  const navigateAfterLogin = useCallback(
    async (signedInUser: User | null) => {
      const { resolvePostLoginDestination } = await import(
        "@/lib/services/post-login-destination"
      );

      let capabilities: string[] = [];
      try {
        const { organisationApiClient } = await import(
          "@/lib/api/clients/organisation-api-client"
        );
        const result =
          await organisationApiClient.getMyOrganisationCapabilities();
        capabilities = result.capabilities ?? [];
      } catch {
        // Fail closed to the ordinary destination.
      }

      let explicit: string | null = null;
      if (typeof window !== "undefined") {
        explicit = window.localStorage.getItem("redirectAfterLogin");
        if (explicit) window.localStorage.removeItem("redirectAfterLogin");
      }

      router.push(
        resolvePostLoginDestination(signedInUser, capabilities, explicit)
      );
    },
    [router]
  );

  // Backward compatibility implementations that delegate to useAuthService
  const loginWithCredentials = useCallback(
    async (credentials: AuthCredentials) => {
      try {
        const tokenResponse = await authService.login({
          email: credentials.email,
          password: credentials.password,
        });

        if (tokenResponse) {
          // Store access token immediately so subsequent authenticated requests work
          if (typeof window !== "undefined") {
            localStorage.setItem(
              "auth_access_token",
              tokenResponse.access_token
            );

            // Store token expiry if provided
            if (tokenResponse.expires_in) {
              const expiryDate = new Date();
              expiryDate.setSeconds(
                expiryDate.getSeconds() + tokenResponse.expires_in
              );
              localStorage.setItem(
                "auth_token_expiry",
                expiryDate.toISOString()
              );
            }

            // Store refresh token if provided
            if (tokenResponse.refresh_token) {
              localStorage.setItem(
                "auth_refresh_token",
                tokenResponse.refresh_token
              );
            }
          }

          // Now get the user information from the backend using the stored tokens
          let signedInUser: User | null = null;
          try {
            const { UserApiClient } = await import(
              "@/lib/api/clients/user-api-client"
            );
            const userApiClient = new UserApiClient();
            const user = await userApiClient.getMe();

            if (user) {
              signedInUser = user;
              // Store complete auth data with user information
              const { storeAuthData } = await import("@/lib/http/auth-utils");
              storeAuthData(tokenResponse, user);
            }
          } catch (userError) {
            console.error("Failed to fetch user data after login:", userError);
          }

          // Refresh auth context after successful login and complete data storage
          await contextActions.refreshAuth();

          // …and finally MOVE. Signing in successfully and staying on the
          // login form is not a state anyone wants to be in.
          await navigateAfterLogin(signedInUser);
        }
      } catch (error) {
        console.error("Login failed:", error);
      }
    },
    [authService, contextActions, navigateAfterLogin]
  );

  const signupWithCredentials = useCallback(
    async (data: SignupData) => {
      try {
        const registerData: {
          email: string;
          password: string;
          confirm_password: string;
          full_name?: string;
        } = {
          email: data.email,
          password: data.password,
          confirm_password: data.confirmPassword,
        };

        if (data.fullName) {
          registerData.full_name = data.fullName;
        }

        const user = await authService.register(registerData);
        if (user) {
          // Registration successful - user needs to verify email before being fully authenticated
          // Auth context will handle the authentication state appropriately
          await contextActions.refreshAuth();
        }
      } catch (error) {
        console.error("Registration failed:", error);
      }
    },
    [authService, contextActions]
  );

  const requireAuth = useCallback(
    (redirectTo?: string) => {
      if (!contextState.auth.isAuthenticated) {
        const targetPath =
          AuthRedirectService.getUnauthenticatedRedirectPath(redirectTo);
        AuthRedirectService.redirectWithRouter(router, targetPath);
      }
    },
    [contextState.auth.isAuthenticated, router]
  );

  const actions = useMemo(
    (): AuthActions => ({
      loginWithCredentials,
      signupWithCredentials,
      signOut: contextActions.signOut,
      clearError: contextActions.clearError,
      requireAuth,
    }),
    [
      loginWithCredentials,
      signupWithCredentials,
      contextActions.signOut,
      contextActions.clearError,
      requireAuth,
    ]
  );

  return useMemo(
    () => ({
      state,
      actions,
      isInPublicOrganization: contextState.publicOrg.isInPublicOrganization,
    }),
    [state, actions, contextState.publicOrg.isInPublicOrganization]
  );
}
